// frontend/src/services/demoApi.ts
// API service for public demo functionality

import { API_CONFIG } from '../utils/config';
import { decodeHtmlEntitiesDeep } from '../utils/textHelpers';
import { buildMarketRequestHeaders } from '../utils/marketContext';

import logger from '../utils/logger';
const API_BASE = API_CONFIG.BACKEND_URL;

export interface QuickDemoRequest {
  idea: string;
  location?: string;
  sourcePage?: string;
}

export interface QuickDemoResponse {
  success: boolean;
  demoId: number;
  actionReceipt: string | null;
  idea: string;
  overallScore: number;
  scoreLabel: string;
  marketSize: string | null;
  countryCode?: string | null;
  currencyCode?: string | null;
  marketName?: string | null;
  marketFallback?: boolean;
  // NEW: 4 distinct fields for unified card display
  painPoint?: string;
  keyInsight?: string;
  whyNow?: string;
  keyOpportunity?: string;
  // AI-generated business metrics (NO HARDCODED VALUES)
  businessMetrics?: {
    arrPotential?: string | null;
    avgCustomerValue?: string | null;
    targetCustomers?: string | null;
    timeToRevenue?: string | null;
    marketCaptureRate?: string | null;
  } | null;
  // Legacy: for backward compatibility
  quickInsights: string[];
  analysisLevel: string;
  demosRemaining: number;
  estimatedTime: string | null;
  // ✅ Real evidence sources from AI analysis
  evidenceSummary?: {
    totalSources: number;
    platforms: string[];
    sampleSources: Array<{
      quote: string;
      url: string;
      platform: string;
    }>;
  };
  // Measured demo metrics; unsupported commercial fields fail closed as null.
  valueProposition?: {
    monthlyPrice: string | null;
    competitorPrice: string | null;
    consultingPrice: string | null;
    timeComparison: string | null;
    companiesValidated: string | null;
    ideasAnalyzed?: string | null;
    averageAnalysisTimeMinutes?: number | null;
  };
  isSample?: boolean;
}

export interface PublicDemo {
  demoId: number;
  idea: string;
  overallScore: number;
  scoreLabel: string;
  marketSize: string | null;
  countryCode?: string | null;
  currencyCode?: string | null;
  marketName?: string | null;
  marketFallback?: boolean;
  // NEW: 4 distinct fields for unified card display
  painPoint?: string;
  keyInsight?: string;
  whyNow?: string;
  keyOpportunity?: string;
  // AI-generated business metrics (NO HARDCODED VALUES)
  businessMetrics?: {
    arrPotential?: string | null;
    avgCustomerValue?: string | null;
    targetCustomers?: string | null;
    timeToRevenue?: string | null;
    marketCaptureRate?: string | null;
  } | null;
  // Legacy: for backward compatibility
  quickInsights: string[];
  viewCount: number;
  upvoteCount: number;
  createdAt: string;
  // ✅ Real evidence sources from AI analysis
  evidenceSummary?: {
    totalSources: number;
    platforms: string[];
    sampleSources: Array<{
      quote: string;
      url: string;
      platform: string;
    }>;
  };
  // Measured demo metrics; unsupported commercial fields fail closed as null.
  valueProposition?: {
    monthlyPrice: string | null;
    competitorPrice: string | null;
    consultingPrice: string | null;
    timeComparison: string | null;
    companiesValidated: string | null;
    ideasAnalyzed?: string | null;
    averageAnalysisTimeMinutes?: number | null;
  };
  isSample?: boolean;
}

export interface DemoError {
  error: string;
  message: string;
  remaining?: number;
  upgradeUrl?: string;
}

/**
 * Submit a quick demo analysis
 */
export async function submitQuickDemo(
  request: QuickDemoRequest,
  language: string = 'en',
): Promise<QuickDemoResponse> {
  const headers = await buildMarketRequestHeaders(language, {
    'Content-Type': 'application/json',
  });
  const response = await fetch(`${API_BASE}/api/demo/quick`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  const data = await response.json();

  if (!response.ok) {
    const err = new Error((data && (data.error || data.message)) || `HTTP ${response.status}`);
    Object.assign(err, { status: response.status, ...data });
    throw err;
  }

  return decodeHtmlEntitiesDeep(data);
}

/**
 * Get public demos for homepage carousel
 * @param limit - Number of demos to fetch
 * @param language - Optional language filter (e.g., 'en', 'fr', 'es')
 */
export async function getPublicDemos(limit: number = 10, language?: string): Promise<PublicDemo[]> {
  const url = new URL(`${API_BASE}/api/demo/public`);
  url.searchParams.set('limit', limit.toString());
  if (language) {
    url.searchParams.set('language', language);
  }
  
  const headers = await buildMarketRequestHeaders(language || 'en');
  const response = await fetch(url.toString(), { headers });
  let data: { demos?: PublicDemo[]; error?: string; message?: string } = {};
  try {
    data = await response.json();
  } catch {
    logger.warn('Public demos unavailable; using landing fallback demos.');
    return [];
  }

  if (!response.ok) {
    logger.warn('Public demos unavailable; using landing fallback demos.', data.error || data.message || response.status);
    return [];
  }

  return decodeHtmlEntitiesDeep(data.demos || []);
}

/**
 * Get single demo by ID
 */
export async function getDemoById(demoId: number, language: string = 'en') {
  const headers = await buildMarketRequestHeaders(language);
  const response = await fetch(`${API_BASE}/api/demo/${demoId}`, { headers });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch demo');
  }

  return decodeHtmlEntitiesDeep(data.demo);
}

/**
 * Track demo event (viewed, expanded, converted)
 */
export async function trackDemoEvent(
  demoId: number,
  event: 'viewed' | 'expanded' | 'converted',
  actionReceipt?: string,
): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/demo/${demoId}/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ event, actionReceipt }),
    });
  } catch (err) {
    // Silent fail - analytics shouldn't break UX
    logger.warn('Failed to track demo event:', err);
  }
}
